/**
 * Client-side upload validation.
 *
 * Caps are advisory — the backend re-validates everything. They exist so
 * the UI doesn't lock up while serialising a huge file into a FormData
 * blob, and to give the user an immediate inline error instead of
 * waiting for a 422 from the upload endpoint.
 *
 * Pure functions — no React, no DOM — so they're unit-testable from
 * Vitest without mounting a component.
 */
import type { UploadSlot } from "./tools";

// 50 MB / 5 MB are well above any real netlist + BOM we've seen on large
// boards. If a user legitimately exceeds these, bump the caps here AND
// the corresponding nginx / FastAPI multipart limit on the server.
export const MAX_NETLIST_BYTES = 50 * 1024 * 1024;
export const MAX_BOM_BYTES = 5 * 1024 * 1024;
export const MAX_FILENAME_LEN = 255;

function _hasExt(name: string, exts: string[]): boolean {
  const lower = name.toLowerCase();
  return exts.some((e) => lower.endsWith(e.toLowerCase()));
}

function _formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Extensions a slot accepts, parsed from its comma-separated `accept`. */
export function slotExtensions(slot: UploadSlot): string[] {
  return slot.accept
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Validate a file against the policy for a given upload slot. Returns a
 * human-readable error string, or null if the file is OK.
 *
 * Browsers already enforce `accept` on file pickers and drop zones in
 * most cases, but a rename (or a drag-drop) can bypass that — this is
 * the second line of defence.
 */
export function validateSlotFile(file: File, slot: UploadSlot): string | null {
  if (file.name.length > MAX_FILENAME_LEN) {
    return `Filename is too long (${file.name.length} chars; max ${MAX_FILENAME_LEN}).`;
  }
  const exts = slotExtensions(slot);
  if (exts.length > 0 && !_hasExt(file.name, exts)) {
    return `Expected ${exts.join(" or ")} for ${slot.label} (got "${file.name}").`;
  }
  const cap = slot.cap === "bom" ? MAX_BOM_BYTES : MAX_NETLIST_BYTES;
  if (file.size > cap) {
    return `${slot.label} is too large (${_formatBytes(file.size)}; max ${_formatBytes(cap)}).`;
  }
  if (file.size === 0) {
    return `${slot.label} is empty.`;
  }
  return null;
}
